/*
 * FarmSteady — sticky bottom ATC visibility controller.
 *
 * Listens for the main product buy-box leaving the viewport, then reveals the
 * sticky chip. Hides it again when the buy-box returns. Per brand-system
 * motion principles, this confirms useful information (the user has scrolled
 * past the buy-box) rather than decorating.
 *
 * Selector strategy:
 *   - Watches the first [data-fs-sticky-atc-trigger] element on the page.
 *     If absent, falls back to product-form (Dawn's <product-form> custom element).
 *   - The sticky chip is the first [data-fs-sticky-atc] element.
 *
 * Also wires the chip's form to the active variant. When a variant changes on
 * the main buy-box (Dawn dispatches a 'variant:change' or updates a hidden
 * input), we mirror the variant id into the chip's hidden input.
 */

(function () {
  'use strict';

  const READY_STATES = ['interactive', 'complete'];

  function init() {
    const chip = document.querySelector('[data-fs-sticky-atc]');
    if (!chip) return;

    const trigger =
      document.querySelector('[data-fs-sticky-atc-trigger]') ||
      document.querySelector('product-form') ||
      document.querySelector('.product-form');
    if (!trigger || !('IntersectionObserver' in window)) {
      // No trigger or no IO support — leave chip hidden.
      return;
    }

    chip.hidden = false;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Show the chip only after the shopper has scrolled PAST the
        // buy-box — it left the viewport via the TOP edge (bottom < 0).
        // "Anywhere off-screen" isn't enough: on mobile the buy-box
        // starts below the fold, which made the capsule appear instantly
        // at page load instead of sliding up after some scrolling
        // (design review 2026-07-17).
        //
        // Observed against the full viewport (no rootMargin): a
        // top-line-only root can be jump-scrolled straight across
        // without ever intersecting, so the observer never fires on
        // fast flicks. Enter/leave of the whole viewport always fires,
        // and the leave side tells us which state to set.
        if (!entry.isIntersecting && entry.boundingClientRect.bottom < 0) {
          chip.dataset.visible = 'true';
        } else {
          chip.dataset.visible = 'false';
        }
      },
      { threshold: 0 }
    );

    observer.observe(trigger);

    // Mirror variant changes from the main form into the chip.
    const chipVariantInput = chip.querySelector('[data-fs-sticky-atc-variant]');
    if (chipVariantInput) {
      // Dawn dispatches no standard variant:change event — instead, the hidden
      // input.id (within the main product form) is updated by variant-selects.js.
      // Poll-on-input as a robust catch-all.
      const mainVariantInput = document.querySelector(
        'product-info input[name="id"], product-form input[name="id"], .product-form input[name="id"]'
      );
      if (mainVariantInput) {
        const sync = () => {
          if (mainVariantInput.value && mainVariantInput.value !== chipVariantInput.value) {
            chipVariantInput.value = mainVariantInput.value;
          }
        };
        // Sync on input + on a slow interval as backup (some Shopify scripts
        // replace inputs rather than modify them).
        mainVariantInput.addEventListener('change', sync);
        const interval = setInterval(sync, 1000);
        // Clean up if page unloads
        window.addEventListener('beforeunload', () => clearInterval(interval));
      }
    }
  }

  if (READY_STATES.includes(document.readyState)) {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
